<script setup>
import { ref, computed, watch, onMounted, provide } from "vue";
import { useVuetify } from "../Composables/useVuetify.js";
import { VNavigationDrawer, VBottomSheet } from "vuetify/lib";
import TipTap from "../Components/Editor/TipTap.vue";
import TipTapReader from "../Components/Editor/TipTapReader.vue";
import { router, usePage, Link } from "@inertiajs/vue2";
import axios from "axios";
import CRMNote from "../Components/CRM/CRMNote.vue";
import CRMAutomation from "../Components/CRM/CRMAutomation.vue";
import ManageLead from "../Components/Guest/ManageLead.vue";

// Provide/Inject

provide("getCrmClient", getCrmClient);
provide("closeCrm", reset);

// Composables

const breakpoint = useVuetify().breakpoint;

// Props

const props = defineProps({
    crmClient: {
        type: Object,
        default: () => {},
        required: false,
    },
    errors: {
        type: Object,
        default: () => {},
        required: false,
    },
});

// Reactive State

const show = ref(false);
const messages = ref([]);
const message = ref("");
const panels = ref([]);
const olliebot = ref("Subscribed");
const showToast = ref(false);
const loading = ref(false);
const manageLead = ref(false);

// Watchers

watch(
    () => props.crmClient.id,
    async (value) => {
        init(value);
    }
);

watch(breakpoint, (value, oldValue) => {
    if (value.smAndDown && show.value) {
        document.body.classList.add("dialog-open-fix");
    } else {
        document.body.classList.remove("dialog-open-fix");
    }
});

// Computed

const isLead = computed(() => {
    return !props.crmClient.loginable;
});

const getComponent = computed(() => {
    const mobile = breakpoint.mdAndDown;

    /*if (mobile) {
        return {
            component: VBottomSheet,
            props: {
                fullscreen: true,
                width: "100%",
                contentClass: "peek half-round",
            },
        };
    }*/

    return {
        component: VNavigationDrawer,
        props: {
            app: true,
            right: true,
            touchless: true,
            width: 350,
            permanent: show.value && !mobile,
            "disable-resize-watcher": true,
        },
    };
});

const actions = computed(() => {
    return {
        communication: [
            {
                title: "Email",
                icon: "email",
                disabled: false,
                handle: () => {
                    router.visit(
                        route("conversation.search", {
                            recipients: [
                                {
                                    messagable_id: props.crmClient.id,
                                    messagable_type: props.crmClient.type,
                                },
                            ],
                            channel: "email",
                        })
                    );
                },
            },
            {
                title: "SMS",
                icon: "sms",
                disabled: false,
                handle: () => {
                    router.visit(
                        route("conversation.search", {
                            recipients: [
                                {
                                    messagable_id: props.crmClient.id,
                                    messagable_type: props.crmClient.type,
                                },
                            ],
                            channel: "sms",
                        })
                    );
                },
            },
            {
                title: "Call",
                icon: "phone",
                disabled: true,
                handle: () => {
                    router.visit(
                        route("conversation.search", {
                            recipients: [
                                {
                                    messagable_id: props.crmClient.id,
                                    messagable_type: props.crmClient.type,
                                },
                            ],
                            channel: "voice",
                        })
                    );
                },
            },
        ],
        manage: [
            {
                title: "Edit",
                icon: "edit",
                handle: () => {
                    manageLead.value = true;
                },
            },
            /*{
                title: "Delete",
                icon: "delete",
                class: "error--text",
                handle: () => {
                    console.log("Delete");
                },
            },*/
        ],
    };
});

const leadDetails = computed(() => {
    return {
        location: props.crmClient.location?.name,
        instrument: props.crmClient.instrument?.name,
        program: props.crmClient.program?.title,
        source: props.crmClient.source,
        promo: props.crmClient.promo,
        campaign_name: props.crmClient.campaign_name,
    };
});

const leadRelationships = computed(() => {
    return (
        props.crmClient.children?.map((child) => {
            return {
                id: child.id,
                name: child.name,
                type: child.type,
                details: {
                    relation: child.inverse,
                    birthday: child.birthdayHelper,
                    instrument: child.instrument?.name,
                    program: child.program?.title,
                },
            };
        }) ?? []
    );
});

// Methods

function getCrmClient(data, loadingOnly = false) {
    // Handle converted leads
    if (data.user_id) {
        data.id = data.user_id;
        data.type = "App\\Models\\User";
    }

    loading.value = true;
    show.value = true;
    if (loadingOnly) return;

    router.get(
        route("crm.client.show", { id: data.id, type: data.type }),
        {},
        {
            only: ["crmClient"],
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                init(props.crmClient?.id);
            },
        }
    );
}

function init(value) {
    if (!value) {
        show.value = false;
        return;
    }
    show.value = true;
    loading.value = false;
    panels.value = isLead.value
        ? [
              //0,
              //leadRelationships?.value?.length ? 1 : null,
              props.crmClient?.active_campaign ? 2 : null,
              props.crmClient?.crm_note?.length ? 3 : null,
          ].filter((e) => e != null)
        : [
              props.crmClient?.active_campaign ? 1 : null,
              props.crmClient?.crm_note?.length ? 2 : null,
          ].filter((e) => e != null);
    //getMessages();
}

function getMessages() {
    if (!props.crmClient.id) return;
    axios.get(`/api/twilio/messages/${props.crmClient.id}`).then((response) => {
        messages.value = response.data;
    });
}

function submit() {
    axios
        .post(`/api/twilio/messages/${props.crmClient.id}`, {
            message: message.value,
            type: "sms",
        })
        .then((response) => {
            messages.value = [{ ...messages.value }, response.data];
        });
}

function closeManageLead(successEvent = null) {
    if (successEvent) {
        showToast.value = successEvent;
    }
    setTimeout(() => {
        manageLead.value = false;
    }, 300);
}

function reset() {
    show.value = false;
    setTimeout(() => {
        router.delete(route("crm.client.destroy"), {
            preserveState: true,
            preserveScroll: true,
            only: ["crmClient"],
        });
    }, 300);
}

onMounted(() => {
    if (!props.crmClient.id) return;
    if (breakpoint.smAndDown) return;
    init(props.crmClient.id);
});
</script>

<template>
    <v-app>
        <slot />
        <component
            :is="getComponent.component"
            v-bind="{ ...getComponent.props }"
            v-model="show"
            :class="[
                {
                    'shadow-left': breakpoint.mdAndUp,
                    'z-drawer':
                        usePage().component == 'Inbox' && breakpoint.smAndDown,
                },
            ]"
            class="crm-drawer"
        >
            <v-card
                :key="crmClient?.id"
                height="100%"
                :class="{ 'half-round': breakpoint.smAndDown }"
                class="crm-card soli-grey"
            >
                <v-container class="flex flex-col h-full w-full">
                    <div class="crm-content safe-bottom">
                        <v-btn
                            icon
                            color="dark"
                            class="modal-close-btn"
                            @click="reset()"
                            ><v-icon>{{
                                !breakpoint.md ? "close" : "exit_to_app"
                            }}</v-icon></v-btn
                        >
                        <v-card-title
                            class="crm-title flex flex-col flex-grow-0 align-start gap-0"
                        >
                            <div class="soli-caption">
                                {{
                                    `${isLead ? "Lead" : "Client"} Information`
                                }}
                            </div>

                            <Link
                                v-if="!loading"
                                :href="
                                    isLead
                                        ? `/dashboard/reports/leads/${crmClient.id}`
                                        : `/profile/${crmClient.id}/account`
                                "
                            >
                                {{ crmClient.name }}
                            </Link>
                            <v-skeleton-loader
                                v-if="loading"
                                class="w-full dense-skeleton"
                                type="card-heading"
                            />
                        </v-card-title>
                        <v-divider />
                        <v-skeleton-loader
                            v-if="loading"
                            class="w-full"
                            type="list-item-avatar-three-line"
                        />
                        <div
                            v-if="!loading && crmClient?.id"
                            class="crm-body divide-y"
                        >
                            <v-card-text class="crm-contact-details gap-2">
                                <div class="flex">
                                    <div class="flex items-center">
                                        <v-list-item-avatar>
                                            <v-img
                                                v-if="
                                                    crmClient.profile_photo_url ||
                                                    crmClient.profile_photo_path
                                                "
                                                :src="
                                                    crmClient.profile_photo_path
                                                        ? `/api/image-service/40x40/${crmClient.profile_photo_path}`
                                                        : crmClient.profile_photo_url
                                                "
                                            ></v-img>
                                            <v-icon
                                                v-else
                                                size="48"
                                                color="dark"
                                                >account_circle</v-icon
                                            >
                                        </v-list-item-avatar>
                                        <div class="grid">
                                            <v-list-item-subtitle
                                                class="line-clamp-1"
                                            >
                                                {{ crmClient.email }}
                                            </v-list-item-subtitle>
                                            <v-list-item-subtitle>
                                                {{ crmClient.phone }}
                                            </v-list-item-subtitle>
                                        </div>
                                    </div>
                                    <v-spacer />
                                    <div class="h-full my-auto">
                                        <v-menu min-width="120" offset-y left>
                                            <template v-slot:activator="{ on }">
                                                <v-btn icon v-on="on">
                                                    <v-icon>more_vert</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item
                                                    v-for="action in actions.manage"
                                                    :key="action.title"
                                                    :class="action.class"
                                                    :disabled="
                                                        !crmClient.belongsToLocation
                                                    "
                                                    @click="action.handle"
                                                >
                                                    <v-list-item-title>
                                                        {{ action.title }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </div>
                                <div class="flex gap-4">
                                    <v-btn
                                        v-for="action in actions.communication"
                                        :key="action.title"
                                        small
                                        rounded
                                        elevation="0"
                                        class="soli-grey"
                                        activeClass="primary--text"
                                        :disabled="action.disabled"
                                        @click="action.handle"
                                    >
                                        <v-icon left>{{ action.icon }}</v-icon>
                                        {{ action.title }}
                                    </v-btn>
                                </div>
                            </v-card-text>
                            <div>
                                <v-expansion-panels
                                    accordion
                                    flat
                                    multiple
                                    v-model="panels"
                                    class="flex flex-col divide-y"
                                >
                                    <v-expansion-panel
                                        v-if="isLead"
                                        class="lead-details"
                                    >
                                        <v-expansion-panel-header
                                            class="soli-grey font-semibold"
                                            >Inquiry Details
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content
                                            class="soli-grey"
                                        >
                                            <v-list-item-subtitle
                                                v-for="(
                                                    item, idx
                                                ) in leadDetails"
                                                :key="idx"
                                                class="flex"
                                                ><div class="text-capitalize">
                                                    {{ idx }}
                                                </div>
                                                <v-spacer />
                                                {{ item }}</v-list-item-subtitle
                                            >
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                    <v-expansion-panel
                                        class="lead-relationships"
                                    >
                                        <v-expansion-panel-header
                                            class="soli-grey font-semibold"
                                            >Relationships
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content
                                            class="flex flex-col divide-y"
                                        >
                                            <v-list-item-title
                                                v-for="child in leadRelationships"
                                                :key="child.id"
                                                class="soli-grey"
                                            >
                                                {{ child.name }}
                                                <v-list-item-subtitle
                                                    v-for="(
                                                        item, idx
                                                    ) in child.details"
                                                    :key="idx"
                                                    class="flex soli-grey"
                                                >
                                                    <div
                                                        class="text-capitalize"
                                                    >
                                                        {{ idx }}
                                                    </div>
                                                    <v-spacer />
                                                    {{ item }}
                                                </v-list-item-subtitle>
                                            </v-list-item-title>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                    <v-expansion-panel>
                                        <v-expansion-panel-header
                                            class="soli-grey font-semibold"
                                            >Automation</v-expansion-panel-header
                                        >
                                        <v-expansion-panel-content>
                                            <CRMAutomation
                                                :crmClient="crmClient"
                                                :errors="errors"
                                                :relationships="
                                                    leadRelationships
                                                "
                                                :disabled="
                                                    !crmClient.belongsToLocation ||
                                                    !isLead
                                                "
                                                @toast="showToast = $event"
                                                @hide="
                                                    show = breakpoint.mdAndUp
                                                "
                                            />
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                    <!--<v-expansion-panel>
                                    <v-expansion-panel-header
                                        class="soli-grey font-semibold"
                                        >Conversations</v-expansion-panel-header
                                    >
                                    <v-expansion-panel-content>
                                        <div class="soli-grey crm-chat">
                                            <div
                                                v-for="message in messages"
                                                :key="message.id"
                                            >
                                                <div>
                                                    <v-list-item-title
                                                        class="flex gap-2 items-center"
                                                    >
                                                        {{ lead.name }}
                                                        <v-chip
                                                            x-small
                                                            color="accent"
                                                            class="white--text"
                                                            >{{
                                                                message.type
                                                            }}</v-chip
                                                        >
                                                    </v-list-item-title>
                                                </div>
                                                <TipTapReader
                                                    v-if="message.content"
                                                    :value="message.content"
                                                />
                                                <p v-else>Missed Call</p>
                                            </div>
                                        </div>
                                        <TipTap
                                            v-if="false"
                                            v-model="message"
                                            valid
                                            @submit="submit($event)"
                                        />
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel v-if="false">
                                    <v-expansion-panel-header
                                        class="soli-grey font-semibold"
                                        >Tasks</v-expansion-panel-header
                                    >
                                </v-expansion-panel>-->
                                    <v-expansion-panel>
                                        <v-expansion-panel-header
                                            class="soli-grey font-semibold"
                                            >Notes</v-expansion-panel-header
                                        >
                                        <v-expansion-panel-content>
                                            <CRMNote
                                                :crmClient="crmClient"
                                                :errors="errors"
                                                :disabled="
                                                    !crmClient.belongsToLocation
                                                "
                                                @toast="showToast = $event"
                                            />
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>
                    <v-spacer />
                    <v-btn
                        v-if="isLead"
                        small
                        rounded
                        text
                        color="accent"
                        href="/dashboard/reports/leads"
                        class="mx-4 justify-self-end"
                        ><v-icon small left>chevron_left</v-icon>All
                        Leads</v-btn
                    >
                </v-container>
            </v-card>
            <SoliSnack
                v-if="showToast"
                @hide="showToast = false"
                :time="2000"
                :text="showToast"
                :app="false"
                maxWidth="90%"
            ></SoliSnack>
            <ManageLead
                v-if="manageLead"
                :leadId="crmClient?.lead?.id ?? crmClient.id"
                @reset="closeManageLead()"
                @success="closeManageLead($event)"
                :errors="errors"
            />
            <v-banner
                v-if="!crmClient.belongsToLocation && !loading"
                :value="true"
                single-line
                :app="false"
                color="error white--text"
                class="fixed bottom-0 w-full z-more"
                ><div class="flex justify-center">
                    Client Changed Locations
                </div></v-banner
            >
        </component>
        <div class="z-more" />
    </v-app>
</template>

<style scoped>
.crm-drawer {
    /*z-index: 205;*/
    z-index: 10;
}

.crm-content {
    max-height: 100% !important;
    max-height: 100svh !important;
    overflow-y: auto;
}

.z-drawer {
    z-index: 204;
}

.z-more {
    z-index: 250;
    position: absolute;
    bottom: 0;
    right: 0;
}

.crm-title {
    padding-top: 2px;
}

.crm-card {
    position: relative;
    display: flex;
    flex-direction: column;
    border: none;
    height: 100%;
    padding-left: 4px;
    padding-bottom: 8px;
}

.crm-body {
    display: flex;
    flex-direction: column;
    flex: 1;
}

::-webkit-scrollbar {
    display: none;
}

.crm-chat {
    max-height: 300px;
    overflow-y: auto;
    flex-direction: column-reverse;
    justify-content: flex-start;
    flex: 0;
    display: flex;
}

.crm-contact-details {
    display: flex;
    flex-direction: column;
    flex: 0;
}

::v-deep .v-expansion-panel-header {
    padding: 1rem;
    min-height: 2rem;
}

::v-deep .v-expansion-panel-content__wrap {
    padding: 1rem;
    padding-top: 0px;
}

::v-deep(.dense-skeleton .v-skeleton-loader__heading) {
    margin: 4px 0px !important;
}
</style>
